/**
 * Node Profile Data Entry Point
 *
 * Exports all CPU compute and infrastructure node profiles.
 * Merges built-in profiles with custom profiles from localStorage.
 */

import type { NodeProfile, NodeRole } from '../../types/additional-nodes';
import { getCustomNodeProfilesSync } from '../../services/customProfileStorage';

// Export profiles
export * from './cpu-compute-profiles';
export * from './infrastructure-profiles';

// Import for aggregation
import { CPU_COMPUTE_PROFILES } from './cpu-compute-profiles';
import { INFRASTRUCTURE_PROFILES } from './infrastructure-profiles';

/**
 * Built-in node profiles (read-only)
 */
export const BUILTIN_NODE_PROFILES: readonly NodeProfile[] = [
  ...CPU_COMPUTE_PROFILES,
  ...INFRASTRUCTURE_PROFILES,
] as const;

/**
 * Get all node profiles including custom ones from localStorage
 */
function getAllNodeProfilesWithCustom(): NodeProfile[] {
  const customProfiles = getCustomNodeProfilesSync();
  return [...BUILTIN_NODE_PROFILES, ...customProfiles];
}

/**
 * All available node profiles (built-in + custom)
 */
export const ALL_NODE_PROFILES: NodeProfile[] = getAllNodeProfilesWithCustom();

/**
 * Node lookup map for fast ID-based access (refreshed dynamically)
 */
function createNodeMap(): Map<string, NodeProfile> {
  const allProfiles = getAllNodeProfilesWithCustom();
  return new Map(allProfiles.map((node) => [node.id, node]));
}

/**
 * Gets a node profile by ID (checks both built-in and custom)
 */
export function getNodeById(id: string): NodeProfile | undefined {
  const nodeMap = createNodeMap();
  return nodeMap.get(id);
}

/**
 * Gets node profiles by role (includes custom profiles)
 */
export function getNodesByRole(role: NodeRole): NodeProfile[] {
  const allProfiles = getAllNodeProfilesWithCustom();
  return allProfiles.filter((node) => node.role === role);
}

/**
 * Gets CPU compute node profiles (includes custom)
 */
export function getCPUComputeNodes(): NodeProfile[] {
  return getNodesByRole('cpu-compute');
}

/**
 * Gets infrastructure node profiles (includes custom)
 */
export function getInfrastructureNodes(): NodeProfile[] {
  return getNodesByRole('infrastructure');
}

/**
 * Gets nodes by vendor (includes custom)
 */
export function getNodesByVendor(vendor: string): NodeProfile[] {
  const allProfiles = getAllNodeProfilesWithCustom();
  return allProfiles.filter(
    (node) => node.vendor.toLowerCase() === vendor.toLowerCase()
  );
}

/**
 * Gets nodes by rack units (includes custom)
 */
export function getNodesByRackUnits(rackUnits: number): NodeProfile[] {
  const allProfiles = getAllNodeProfilesWithCustom();
  return allProfiles.filter((node) => node.rackUnits === rackUnits);
}

/**
 * Validates that a node ID exists (checks custom too)
 */
export function isNodeIdValid(id: string): boolean {
  const nodeMap = createNodeMap();
  return nodeMap.has(id);
}

/**
 * Gets all node IDs (includes custom)
 */
export function getAllNodeIds(): string[] {
  const allProfiles = getAllNodeProfilesWithCustom();
  return allProfiles.map((node) => node.id);
}
